import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import {
  type CreateWorkerCredentialRequestRequest,
  createWorkerCredentialRequestRequestSchema,
  type CreateWorkerCredentialRequestResponse,
  createWorkerCredentialRequestResponseSchema,
} from "./schema";
import { WORKER_CREDENTIALS_ENDPOINT_URL } from "./useGetWorkerCredentials";

export function getWorkerCredentialRequestEndpointUrl(workplaceId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/worker-credential-requests`;
}

export function useCreateWorkerCredentialRequest(
  options: UseMutationOptions<
    CreateWorkerCredentialRequestResponse,
    unknown,
    CreateWorkerCredentialRequestRequest
  > = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body) => {
      const { id: workplaceId } = body.data.relationships.workplace.data;
      const response = await post({
        url: getWorkerCredentialRequestEndpointUrl(workplaceId),
        requestSchema: createWorkerCredentialRequestRequestSchema,
        responseSchema: createWorkerCredentialRequestResponseSchema,
        data: body,
      });
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      const { id: workplaceId } = variables.data.relationships.workplace.data;
      // invalidate any cached worker credentials to ensure fresh fetch next time
      void queryClient.invalidateQueries({ queryKey: [WORKER_CREDENTIALS_ENDPOINT_URL] });
      void queryClient.invalidateQueries({
        queryKey: [
          `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/worker-credentials`,
        ],
      });
      options.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}
